import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { useConciergeNumber, useConciergePhone, formatWaDisplay, telHref } from "../lib/concierge";
import SiteLayout from "../components/site/SiteLayout";
import SocialLinks from "../components/site/SocialLinks";
import EnquiryForm from "../components/site/EnquiryForm";
import TestimonialsCarousel from "../components/site/TestimonialsCarousel";
import NewsletterSignup from "../components/site/NewsletterSignup";
import { CarbonCalculator } from "../components/site/CarbonCalculator";
import Seo from "../components/site/Seo";
import TrendingPinsWidget from "../components/site/TrendingPinsWidget";
import HomeFaq, { HOME_FAQ_JSONLD } from "../components/site/HomeFaq";
import HomeTrustStrip from "../components/site/HomeTrustStrip";
import { SignatureExperiences } from "../components/site/SignatureExperiences";
import LazyMount from "../components/site/LazyMount";
import useRevealOnScroll from "../lib/useRevealOnScroll";
import api, { assetUrl } from "../lib/api";
import {
    Leaf, Globe2, HandHeart, Wind, Award, Quote, ArrowRight,
    Heart, Compass, Castle, Waves, Bird, Crown, Sparkles, Snowflake, Wine, Trophy,
    Anchor, Plane, Ship, Flag,
} from "lucide-react";

// Hero image — served from Unsplash with `auto=format` (WebP/AVIF when supported)
// and a cap of w=1600 q=70 to keep the LCP image well under 200 KiB on mobile.
// PSI flagged the previous unconstrained URL as 8.2 MiB / 4764×9504 — the
// largest single byte cost on the page.
const heroImage =
    "https://images.unsplash.com/photo-1749198118821-0794bb5340b3?auto=format&fit=crop&w=1600&q=70";
const heroImageMobile =
    "https://images.unsplash.com/photo-1749198118821-0794bb5340b3?auto=format&fit=crop&w=900&q=65";
const heroVideo =
    "https://customer-assets.emergentagent.com/job_eco-luxury-preview-1/artifacts/35li40p3_result_final_v4%20%281%29.mp4";

const impactItems = [
    { icon: Leaf, title: "1000+ trees planted", body: "Verified via Tree-Nation forest — every booking funds a tree, with monthly Net-Zero-Team replants." },
    { icon: Wind, title: "80+ tonnes CO₂ offset", body: "Cumulative carbon-neutralisation across guest journeys, web traffic and Net-Zero operations." },
    { icon: HandHeart, title: "500+ satisfied guests", body: "Hand-drafted itineraries across India, Asia, Africa, Europe, Americas, Oceania and Antarctica." },
    { icon: Globe2, title: "4 city presence", body: "Mumbai · Thane · Jaipur · Vizag — local concierges, local language, local trust." },
];

const signatureCollections = [
    {
        icon: Heart, title: "Wellness Retreats", body: "Yoga, Ayurveda, Panchakarma — fully curated.",
        href: "/retreats", testKey: "wellness",
        thumb: "/api/assets/img/himalaya-meditation-retreat.jpg",
    },
    {
        icon: Bird, title: "Wildlife Safaris", body: "Tigers, snow leopards, Big Five.",
        href: "/experiences?theme=wildlife", testKey: "wildlife",
        thumb: "https://images.unsplash.com/photo-1561731216-c3a4d99437d5?auto=format&fit=crop&w=800&q=70",
    },
    {
        icon: Castle, title: "Heritage Journeys", body: "Rajputana, Kyoto, Koyasan.",
        href: "/experiences?theme=heritage", testKey: "heritage",
        thumb: "https://images.unsplash.com/photo-1524413840807-0c3cb6fa808d?auto=format&fit=crop&w=800&q=70",
    },
    {
        icon: Compass, title: "Adventure Beyond Boundaries", body: "Expedition treks, polar voyages, summit climbs.",
        href: "/experiences?theme=adventure", testKey: "adventure",
        thumb: "/api/assets/img/iceland-ice-cave.jpg",
    },
    {
        icon: Waves, title: "Oceans of Wonder", body: "Galápagos, Seychelles, Norwegian Fjords.",
        href: "/experiences?theme=oceans", testKey: "oceans",
        thumb: "/api/assets/img/galapagos-tortoise.jpg",
    },
    {
        icon: Heart, title: "Dream Honeymoons", body: "Maldives, Mauritius, Kashmir — curated 'Dil Se'.",
        href: "/honeymoons", testKey: "honeymoon",
        thumb: "/api/assets/img/honeymoon-maldives-villa.jpg",
    },
    {
        icon: Crown, title: "Premium India Journeys", body: "Golden Triangle, Royal Rajasthan, Palace on Wheels.",
        href: "/tours", testKey: "premium-tours",
        thumb: "/api/assets/img/golden-triangle-premium.jpg",
    },
    {
        icon: Sparkles, title: "Spiritual Sojourns", body: "Ayodhya, Kashi, Chardham — sacred India, unhurried.",
        href: "/tours/spiritual-sojourn-ayodhya-kashi", testKey: "spiritual",
        thumb: "/api/assets/img/spiritual-sojourn-premium.jpg",
    },
    {
        icon: Snowflake, title: "Polar & Northern Lights", body: "Aurora hunts, Lapland, Antarctic voyages.",
        href: "/tours/northern-lights-arctic", testKey: "polar",
        thumb: "/api/assets/img/northern-lights-premium.jpg",
    },
    {
        icon: Wine, title: "Wine & Culinary Odysseys", body: "Eight wine regions, four continents, one sommelier.",
        href: "/tours/wine-odyssey", testKey: "wine",
        thumb: "/api/assets/img/wine-odyssey-premium.jpg",
    },
    {
        icon: HandHeart, title: "Rural Tourism", body: "Village homestays, pottery & tribal India — from ₹49,999.",
        href: "/tours/rural-tourism", testKey: "rural",
        thumb: "/api/assets/img/rural-tourism-premium.jpg",
    },
    {
        icon: Anchor, title: "Underwater Odyssey", body: "Scuba & freediving — Andamans, Maldives, Raja Ampat.",
        href: "/tours/underwater-odyssey", testKey: "underwater",
        thumb: "/api/assets/img/underwater-odyssey-premium.jpg",
    },
    {
        icon: Plane, title: "Skydive the World", body: "Freefall over Dubai's Palm, the Alps & Queenstown.",
        href: "/tours/skydive-the-world", testKey: "skydive",
        thumb: "/api/assets/img/skydive-premium.jpg",
    },
    {
        icon: Ship, title: "Explora Luxury Cruises", body: "700+ all-suite ocean voyages — concierge-booked.",
        href: "/tours/explora-journeys", testKey: "explora",
        thumb: "/api/assets/img/explora-cruise-premium.jpg",
    },
    {
        icon: Flag, title: "Formula 1 Grand Prix", body: "Monaco, Abu Dhabi & Singapore — Paddock Club weekends.",
        href: "/tours/formula-1-grand-prix", testKey: "formula1",
        thumb: "/api/assets/img/formula1-premium.jpg",
    },
];

// Credentials replacing the old "As Seen In" tile row.
const credentials = [
    {
        src: "/credentials/our-recognition.png",
        alt: "MyEcoTour recognition: India 5000 Best MSME, GSTC, WTN, SME.travel",
        title: "Our Recognition",
        body: "India 5000 Best MSME 2024 Winner · GSTC Member · World Tourism Network · SME.travel · Greenpreneur Award 2025 & 2026.",
        wide: true,
    },
    {
        src: "/credentials/greenpreneur-trophy.png",
        alt: "Greenpreneur Award trophy — Hospitality and Tourism, Whispering Earth LLP",
        title: "Greenpreneur Award — 2025 & 2026",
        body: "Winner of the Greenpreneur Award two years in a row, 2025 & 2026 — Hospitality & Tourism, awarded to Whispering Earth LLP.",
    },
    {
        src: "/credentials/ecopreneur-2026.png",
        alt: "Times Internet Ecopreneur Awards 2026 nominee",
        title: "Ecopreneur Awards 2026 — Nominee",
        body: "Nominated for the Times of India–powered Times Internet Ecopreneur Award 2026.",
        mdWide: true,
    },
    {
        src: "/credentials/certificate.png",
        alt: "India 5000 Best MSME 2024 Certificate of Recognition",
        title: "India 5000 — 2024 Winner",
        body: "Certificate of Recognition issued by INDIA 5000 / Benchmark Trust, TQV (Knowledge Partner), BSE (Supporting Partner).",
    },
    {
        src: "/credentials/msme-trophy.png",
        alt: "India 5000 Best MSME Awards 2024 — Winner badge",
        title: "Quality Excellence",
        body: "Recognised for measurable contribution to customer satisfaction, sustainability, and service excellence.",
    },
    {
        src: "/credentials/msme-badge-laurel.png",
        alt: "India 5000 Best MSME 2024 — laurel-wreath winner badge",
        title: "Verified Operator",
        body: "Officially audited under the INDIA 5000 'Quality Excellence' Programme — Certificate No. 2024010201.",
    },
];

const awards = [
    "INDIA 5000 Best MSME Awards · Winner 2024",
    "Responsible Tourism Award 2024",
    "Best Boutique Operator — India 2023",
];

export default function Home() {
    const conciergeWa = useConciergeNumber();
    const conciergePhone = useConciergePhone();
    const [featured, setFeatured] = useState([]);
    const [journalPosts, setJournalPosts] = useState([]);
    const [tp, setTp] = useState(null);

    useEffect(() => {
        api.get("/experiences", { params: { featured: true } })
            .then((res) => setFeatured(res.data.slice(0, 4)))
            .catch(() => {});
        api.get("/blog/posts", { params: { featured: true, limit: 2 } })
            .then((res) => setJournalPosts(res.data.slice(0, 2)))
            .catch(() => {});
        api.get("/settings/public")
            .then((res) => setTp(res.data))
            .catch(() => {});
    }, []);

    // Reveal-on-scroll observers for two sections below the fold. One-shot
    // each — they trigger the staggered fade-up via index.css `.reveal-stagger`.
    const collections = useRevealOnScroll();

    // Journey pre-fill for the enquiry form (set from Signature cards).
    const [enquiryPrefill, setEnquiryPrefill] = useState("");
    const enquireForJourney = (name) => {
        setEnquiryPrefill(name);
        document.getElementById("home-enquiry")?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <SiteLayout>
            <Seo
                title="Luxury Tour Packages India | Bespoke Eco-Luxury Travel Concierge"
                description="Luxury tour packages across India & beyond — tiger safaris, Maldives honeymoons, Kerala wellness retreats, palace trains. Carbon-neutral, concierge-arranged, from ₹89,999. India 5000 Best MSME 2024."
                keywords="eco luxury travel India, honeymoon packages, sustainable holiday operator, carbon neutral safari, bespoke travel concierge, wildlife wellness heritage adventure ocean tours, Antarctica expedition, Ladakh tour, Sundarbans tiger, Cappadocia balloon, Sri Lanka wellness, Hornbill festival Nagaland, Naga tribes Kohima, Rann Utsav Kutch, Chardham yatra Kedarnath, Maldives honeymoon, Kashmir Gulmarg honeymoon, Northern Lights Norway, Cherry blossom Japan, Swiss cycling, Uluru Australia, Fiji overwater bure, Galápagos cruise, Palawan El Nido Philippines, Coron island hopping, Vietnam Halong Bay cruise, Hoi An lantern town, Hanoi food tour, Pinterest travel pins, MyEcoTour"
                jsonLd={{
                    "@context": "https://schema.org",
                    "@graph": [
                        {
                            "@type": "TravelAgency",
                            "@id": "https://myecotour.in/#agency",
                            "name": "MyEcoTour — Whispering Earth LLP",
                            "url": "https://myecotour.in",
                            "logo": "https://myecotour.in/api/assets/img/rural-tourism-premium.jpg",
                            "description": "Bespoke, carbon-neutral eco-luxury travel concierge. India 5000 Best MSME 2024 · Greenpreneur Award 2025.",
                            "email": "info@myecotour.com",
                            "address": { "@type": "PostalAddress", "addressLocality": "Malad East, Mumbai", "postalCode": "400097", "addressCountry": "IN" },
                            "areaServed": ["Mumbai", "Thane", "Jaipur", "Visakhapatnam", "India", "Worldwide"],
                            "priceRange": "₹₹₹₹",
                            "sameAs": ["https://www.trustpilot.com/review/myecotour.in", "https://in.pinterest.com/myecotour"],
                        },
                        {
                            "@type": "ItemList",
                            "name": "MyEcoTour Signature Collections",
                            "itemListElement": [
                                { "@type": "ListItem", "position": 1, "name": "Wellness Retreats", "url": "https://myecotour.in/retreats" },
                                { "@type": "ListItem", "position": 2, "name": "Wildlife Safaris", "url": "https://myecotour.in/experiences?theme=wildlife" },
                                { "@type": "ListItem", "position": 3, "name": "Heritage Journeys", "url": "https://myecotour.in/experiences?theme=heritage" },
                                { "@type": "ListItem", "position": 4, "name": "Adventure Beyond Boundaries", "url": "https://myecotour.in/experiences?theme=adventure" },
                                { "@type": "ListItem", "position": 5, "name": "Oceans of Wonder", "url": "https://myecotour.in/experiences?theme=oceans" },
                                { "@type": "ListItem", "position": 6, "name": "Dream Honeymoons", "url": "https://myecotour.in/honeymoons" },
                                { "@type": "ListItem", "position": 7, "name": "Premium India Journeys", "url": "https://myecotour.in/tours" },
                                { "@type": "ListItem", "position": 8, "name": "Spiritual Sojourns", "url": "https://myecotour.in/tours/spiritual-sojourn-ayodhya-kashi" },
                                { "@type": "ListItem", "position": 9, "name": "Polar & Northern Lights", "url": "https://myecotour.in/tours/northern-lights-arctic" },
                                { "@type": "ListItem", "position": 10, "name": "Wine & Culinary Odysseys", "url": "https://myecotour.in/tours/wine-odyssey" },
                            ],
                        },
                        HOME_FAQ_JSONLD,
                    ],
                }}
            />
            {/* ======= HERO ======= */}
            <section className="relative h-screen min-h-[640px] -mt-20 flex items-end overflow-hidden" data-testid="hero-section">
                <div className="absolute inset-0">
                    <img
                        src={heroImage}
                        srcSet={`${heroImageMobile} 900w, ${heroImage} 1600w`}
                        sizes="(max-width: 768px) 100vw, 100vw"
                        alt="A serene eco-lodge framed by lush forest"
                        className="w-full h-full object-cover img-warm"
                        loading="eager"
                        fetchpriority="high"
                        decoding="async"
                        width="1600"
                        height="900"
                    />
                    <video
                        autoPlay
                        loop
                        muted
                        playsInline
                        preload="none"
                        poster={heroImageMobile}
                        className="absolute inset-0 w-full h-full object-cover img-warm"
                        data-testid="hero-video"
                    >
                        <source src={heroVideo} type="video/mp4" />
                        {/* Equivalent audio description for screen-reader and
                            Lighthouse accessibility ("video should contain a
                            <track>") — the clip is silent and atmospheric. */}
                        <track
                            kind="descriptions"
                            src="/hero-description.vtt"
                            srcLang="en"
                            label="Scene description (English)"
                            default
                        />
                    </video>
                    <div className="absolute inset-0 bg-gradient-to-b from-black/15 via-black/30 to-black/70" />
                </div>

                {/* India 5000 Award badge */}
                <div className="absolute top-28 right-6 sm:right-12 lg:right-20 hidden sm:block z-10" data-testid="hero-award-badge">
                    <div className="flex items-start gap-3 px-4 py-3 bg-background/90 backdrop-blur border border-accent/40 max-w-xs">
                        <Award size={20} strokeWidth={1.5} className="text-accent mt-0.5 shrink-0" />
                        <div className="text-[10px] leading-snug tracking-wider">
                            <div className="font-semibold text-foreground uppercase">India 5000 Best MSME</div>
                            <div className="text-muted-foreground mt-0.5">Winner · 2024</div>
                        </div>
                    </div>
                    {tp?.trustpilot_enabled && tp?.trustpilot_score ? (
                        <a
                            href="https://www.trustpilot.com/review/myecotour.in"
                            target="_blank"
                            rel="noreferrer"
                            data-testid="hero-trustpilot-badge"
                            className="mt-2 flex items-center gap-2.5 px-4 py-3 bg-background/90 backdrop-blur border border-[#00b67a]/50 hover:border-[#00b67a] transition-colors"
                        >
                            <span className="flex gap-0.5" aria-hidden="true">
                                {[1, 2, 3, 4, 5].map((i) => (
                                    <span key={i} className="w-4 h-4 bg-[#00b67a] flex items-center justify-center">
                                        <svg viewBox="0 0 24 24" className="w-2.5 h-2.5 fill-white"><path d="M12 2l2.9 6.3 6.9.8-5.1 4.7 1.4 6.8L12 17l-6.1 3.6 1.4-6.8L2.2 9.1l6.9-.8z" /></svg>
                                    </span>
                                ))}
                            </span>
                            <span className="text-[10px] leading-snug tracking-wider">
                                <span className="block font-semibold text-foreground uppercase">Rated {tp.trustpilot_score} on Trustpilot</span>
                                {tp.trustpilot_reviews ? <span className="block text-muted-foreground mt-0.5">{tp.trustpilot_reviews} reviews</span> : null}
                            </span>
                        </a>
                    ) : null}
                </div>

                <div className="relative container-page pb-24 sm:pb-32 text-primary-foreground">
                    <div className="max-w-3xl fade-in-up">
                        <div
                            data-testid="hero-trophy-badge"
                            className="inline-flex items-center gap-2.5 mb-5 px-4 py-2 border border-accent/50 bg-black/25 backdrop-blur-sm text-[10px] tracking-[0.28em] uppercase font-semibold text-accent"
                        >
                            <Trophy size={13} strokeWidth={1.5} />
                            Award-winning Conscious Luxury · Greenpreneur 2025 & 2026
                        </div>
                        <p className="overline text-primary-foreground/85 mb-6" data-testid="hero-overline">
                            A Bespoke Travel Concierge · Explore with Purpose
                        </p>
                        <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight" data-testid="hero-title">
                            Bespoke <em className="italic text-accent/95">eco-conscious</em> travel,
                            <br className="hidden sm:block" /> and bespoke eco-luxury tours.
                        </h1>
                        <p className="mt-8 max-w-xl text-lg leading-relaxed text-primary-foreground/85">
                            Wellness, wildlife, heritage, adventure and oceans. Quietly designed journeys for the mindful soul.
                        </p>
                        <div className="mt-10 flex flex-wrap gap-4">
                            <Link
                                to="/experiences"
                                data-testid="hero-cta-primary"
                                className="inline-flex items-center gap-3 px-9 py-4 bg-primary-foreground text-primary text-xs tracking-[0.25em] uppercase font-semibold hover:bg-accent hover:text-accent-foreground transition-colors duration-300"
                            >
                                Explore Our Journeys
                                <ArrowRight size={16} strokeWidth={1.5} />
                            </Link>
                            <a
                                href={telHref(conciergePhone)}
                                data-testid="hero-cta-secondary"
                                className="inline-flex items-center gap-3 px-9 py-4 border border-primary-foreground/50 text-primary-foreground text-xs tracking-[0.25em] uppercase font-semibold hover:border-accent hover:text-accent transition-colors duration-300"
                            >
                                Speak to a Concierge
                            </a>
                        </div>
                        <div className="mt-12 flex flex-wrap gap-x-8 gap-y-2 text-[10px] tracking-[0.3em] uppercase text-primary-foreground/65" data-testid="hero-pillars">
                            <span>Carbon-Neutral Journeys</span>
                            <span>·</span>
                            <span>Net Zero Operations</span>
                            <span>·</span>
                            <span>Net Zero Website</span>
                        </div>
                    </div>
                </div>

                <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-primary-foreground/75 text-[10px] tracking-[0.4em] uppercase">
                    Scroll
                </div>
            </section>

            {/* ======= TRUST STRIP (live signals: trees, Trustpilot, award, carbon-neutral) ======= */}
            <HomeTrustStrip />

            {/* ======= USP / TRUST ======= */}
            <section className="section-padding bg-background" data-testid="usp-section">
                <div className="container-page grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
                    <div className="lg:col-span-5">
                        <p className="overline text-muted-foreground mb-6">Our Philosophy</p>
                        <h2 className="font-serif text-4xl sm:text-5xl tracking-tight leading-[1.1]">
                            Travel as <em className="italic text-accent">a quiet act</em> of care.
                        </h2>
                    </div>
                    <div className="lg:col-span-7 space-y-6 text-lg leading-relaxed text-foreground/85">
                        <p>
                            MyEcoTour exists for guests who refuse to choose between elegance and ethics. We design private journeys with the patience of curators — not the urgency of agents. Where culture meets consciousness.
                        </p>
                        <p className="text-base text-muted-foreground">
                            Every lodge we work with is independently owned, low-impact and bound to a place. Every itinerary is shaped one conversation at a time, with the unhurried sensibility of a concierge who knows the country well.
                        </p>
                        <div className="pt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
                            {awards.map((a) => (
                                <div key={a} className="flex items-start gap-3 text-sm text-muted-foreground border-l border-border pl-4" data-testid="award-card">
                                    <Award size={16} strokeWidth={1.5} className="mt-0.5 text-accent shrink-0" />
                                    <span className="leading-snug">{a}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* ======= NEWSLETTER — "The slow letter" (moved up, below Our Philosophy) ======= */}
            <NewsletterSignup variant="light" />

            {/* ======= SIGNATURE COLLECTIONS ======= */}
            <section className="section-padding bg-background border-t border-border" data-testid="collections-section">
                <div className="container-page">
                    <div className="max-w-2xl mb-14">
                        <p className="overline text-muted-foreground mb-4">Our Signature Collections</p>
                        <h2 className="font-serif text-4xl sm:text-5xl tracking-tight">Fifteen quiet doorways into the world.</h2>
                        <p className="mt-5 text-foreground/70 leading-relaxed">
                            For the discerning traveller. Pick a doorway — we'll handle the unhurried journey through.
                        </p>
                    </div>
                    <div
                        ref={collections.ref}
                        className={`reveal-stagger grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 ${collections.visible ? "is-visible" : ""}`}
                    >
                        {signatureCollections.map((c, idx) => (
                            <Link
                                key={c.title}
                                to={c.href}
                                data-testid={`collection-${c.testKey}`}
                                className="reveal-item group border border-border bg-background hover:border-accent transition-colors flex flex-col h-full overflow-hidden"
                            >
                                <div className="relative h-36 sm:h-40 overflow-hidden bg-secondary">
                                    <img
                                        src={assetUrl(c.thumb)}
                                        alt={c.title}
                                        className="absolute inset-0 w-full h-full object-cover img-warm group-hover:scale-105 transition-transform duration-700"
                                        loading="lazy"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                                    <span className="absolute top-3 left-3 w-10 h-10 border border-white/40 backdrop-blur-sm bg-white/10 flex items-center justify-center text-white">
                                        <c.icon size={18} strokeWidth={1.5} />
                                    </span>
                                    <span className="absolute top-3 right-3 font-serif italic text-sm text-white/90">{String(idx + 1).padStart(2, "0")}</span>
                                </div>
                                <div className="p-6 flex flex-col flex-1">
                                    <h3 className="font-serif text-xl leading-tight mb-3">{c.title}</h3>
                                    <p className="text-xs text-muted-foreground leading-relaxed mb-5">{c.body}</p>
                                    <span className="mt-auto inline-flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase font-semibold text-primary group-hover:text-accent transition-colors">
                                        Explore <ArrowRight size={12} strokeWidth={1.5} />
                                    </span>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* ======= FEATURED EXPERIENCES (Bento) ======= */}
            <section className="section-padding bg-muted" data-testid="featured-section">
                <div className="container-page">
                    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-14">
                        <div>
                            <p className="overline text-muted-foreground mb-4">Curated Journeys</p>
                            <h2 className="font-serif text-4xl sm:text-5xl tracking-tight">A handful of quietly extraordinary journeys.</h2>
                        </div>
                        <Link
                            to="/experiences"
                            data-testid="featured-view-all"
                            className="text-xs tracking-[0.25em] uppercase font-semibold text-primary hover:text-accent transition-colors inline-flex items-center gap-2"
                        >
                            View all experiences <ArrowRight size={14} strokeWidth={1.5} />
                        </Link>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-12 gap-6 lg:gap-8">
                        {featured[0] && (
                            <FeatureCard exp={featured[0]} className="md:col-span-7 md:row-span-2 h-[420px] md:h-[640px]" testId="featured-card-0" large />
                        )}
                        {featured[1] && (
                            <FeatureCard exp={featured[1]} className="md:col-span-5 h-[300px]" testId="featured-card-1" />
                        )}
                        {featured[2] && (
                            <FeatureCard exp={featured[2]} className="md:col-span-5 h-[320px]" testId="featured-card-2" />
                        )}
                        {featured[3] && (
                            <FeatureCard exp={featured[3]} className="md:col-span-12 h-[280px]" testId="featured-card-3" horizontal />
                        )}
                    </div>
                </div>
            </section>

            {/* ======= FROM THE JOURNAL (featured editorial) ======= */}
            {journalPosts.length > 0 && (
                <section className="section-padding bg-background border-t border-border" data-testid="home-journal-section">
                    <div className="container-page">
                        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-14">
                            <div>
                                <p className="overline text-muted-foreground mb-4">From the Journal</p>
                                <h2 className="font-serif text-4xl sm:text-5xl tracking-tight">Stories worth packing for.</h2>
                            </div>
                            <Link
                                to="/journal"
                                data-testid="home-journal-view-all"
                                className="text-xs tracking-[0.25em] uppercase font-semibold text-primary hover:text-accent transition-colors inline-flex items-center gap-2"
                            >
                                Read the journal <ArrowRight size={14} strokeWidth={1.5} />
                            </Link>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
                            {journalPosts.map((p, i) => (
                                <Link
                                    key={p.slug}
                                    to={`/journal/${p.slug}`}
                                    data-testid={`home-journal-card-${i}`}
                                    className="group flex flex-col sm:flex-row bg-secondary/40 border border-border overflow-hidden hover:border-primary transition-colors"
                                >
                                    {p.hero_image_url && (
                                        <div className="sm:w-2/5 h-52 sm:h-auto overflow-hidden shrink-0">
                                            <img src={p.hero_image_url} alt={p.title} loading="lazy" className="w-full h-full object-cover img-warm group-hover:scale-105 transition-transform duration-700" />
                                        </div>
                                    )}
                                    <div className="p-7 flex flex-col justify-between">
                                        <div>
                                            <p className="text-[10px] tracking-[0.25em] uppercase font-semibold text-accent mb-3">
                                                {(p.category || "journal").replace(/-/g, " ")}
                                            </p>
                                            <h3 className="font-serif text-xl leading-snug group-hover:text-primary transition-colors line-clamp-3">{p.title}</h3>
                                            <p className="mt-3 text-sm text-muted-foreground leading-relaxed line-clamp-3">{p.excerpt}</p>
                                        </div>
                                        <span className="mt-5 inline-flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase font-semibold text-primary">
                                            Read the story <ArrowRight size={12} strokeWidth={1.75} />
                                        </span>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* ======= SIGNATURE EXPERIENCES (flagship product spotlight) ======= */}
            <SignatureExperiences onEnquire={enquireForJourney} />

            {/* ======= FINAL CTA / ENQUIRY (moved above credentials) ======= */}
            <section id="home-enquiry" className="section-padding bg-primary text-primary-foreground" data-testid="final-cta-section">
                <div className="container-page grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
                    <div className="lg:col-span-5">
                        <p className="overline text-primary-foreground/55 mb-6">Begin</p>
                        <h2 className="font-serif text-4xl sm:text-5xl tracking-tight leading-[1.1]">
                            Ready for your <em className="italic text-accent">transformative</em> journey?
                        </h2>
                        <p className="mt-8 text-primary-foreground/75 leading-relaxed max-w-md">
                            Tell us a little — a season, a feeling, a destination half-imagined. A concierge will write back personally within one working day.
                        </p>
                        <div className="mt-10 text-xs tracking-[0.25em] uppercase text-primary-foreground/55 space-y-2">
                            <div data-testid="cta-email">info@myecotour.com</div>
                            <a data-testid="cta-phone" href={`https://wa.me/${conciergeWa}`} target="_blank" rel="noopener noreferrer" className="hover:text-accent transition-colors">{formatWaDisplay(conciergeWa)} · WhatsApp</a>
                        </div>
                    </div>
                    <div className="lg:col-span-7">
                        <EnquiryForm key={enquiryPrefill || "default"} variant="dark" initialDestination={enquiryPrefill} />
                    </div>
                </div>
                <div className="container-page mt-20 pt-12 border-t border-primary-foreground/15 flex flex-col md:flex-row md:items-center md:justify-between gap-6" data-testid="home-social-block">
                    <div>
                        <p className="overline text-primary-foreground/55 mb-3">Follow the journey</p>
                        <h3 className="font-serif text-2xl sm:text-3xl tracking-tight">Travel notes, in real time.</h3>
                        <p className="text-primary-foreground/65 mt-2 max-w-md text-sm">
                            Field photos, packing rituals, and small notes from the road — shared on the platforms you already love.
                        </p>
                    </div>
                    <SocialLinks tone="light" showLabels testIdPrefix="home-social" className="md:justify-end" />
                </div>
            </section>

            {/* ======= CREDENTIALS (replaces "As Seen In") ======= */}
            <section className="py-20 bg-background border-t border-border" data-testid="credentials-section">
                <div className="container-page">
                    <div className="text-center max-w-2xl mx-auto mb-14">
                        <p className="overline text-muted-foreground mb-4">Awards · Audits · Memberships</p>
                        <h2 className="font-serif text-3xl sm:text-4xl tracking-tight">Recognised. Certified. Independently audited.</h2>
                        <p className="mt-4 text-sm text-foreground/70 leading-relaxed">
                            We let third-party assessors do our talking — quality excellence audits, sustainability councils, and travel-industry recognition you can verify in 30 seconds.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {credentials.map((cr, i) => (
                            <div
                                key={cr.title}
                                data-testid={`credential-${i}`}
                                className={`group bg-secondary/40 border border-border hover:border-accent transition-colors p-6 flex flex-col ${cr.wide ? "md:col-span-2 lg:col-span-2 lg:row-span-2" : ""} ${cr.mdWide ? "md:col-span-2 lg:col-span-1" : ""}`}
                            >
                                <div className={`relative ${cr.wide ? "h-72 lg:h-[460px]" : "h-44"} overflow-hidden bg-background flex items-center justify-center mb-5`}>
                                    <img
                                        src={cr.src}
                                        alt={cr.alt}
                                        className="max-w-full max-h-full object-contain transition-transform duration-700 group-hover:scale-105"
                                        loading="lazy"
                                    />
                                </div>
                                <h3 className="font-serif text-lg leading-tight mb-2">{cr.title}</h3>
                                <p className="text-xs text-muted-foreground leading-relaxed">{cr.body}</p>
                            </div>
                        ))}
                    </div>
                    <div className="mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-[11px] tracking-[0.18em] uppercase text-foreground/60">
                        <span data-testid="cred-row-india5000">INDIA 5000 · 2024</span>
                        <span aria-hidden>·</span>
                        <span data-testid="cred-row-gstc">GSTC Member</span>
                        <span aria-hidden>·</span>
                        <span data-testid="cred-row-wtn">World Tourism Network</span>
                        <span aria-hidden>·</span>
                        <span data-testid="cred-row-greenpreneur">Greenpreneur 2025 & 2026</span>
                        <span aria-hidden>·</span>
                        <span data-testid="cred-row-ecopreneur">Ecopreneur Awards 2026 Nominee</span>
                        <span aria-hidden>·</span>
                        <a
                            href="https://tree-nation.com/profile/whispering-earth-llp"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="cred-row-treenation"
                            className="hover:text-accent transition-colors"
                        >
                            Tree-Nation forest ↗
                        </a>
                    </div>
                </div>
            </section>

            {/* ======= TRENDING PINS (Pinterest social-proof mood-board) ======= */}
            {/* Below-the-fold: hydrate only when scrolled within 600px of viewport. */}
            <LazyMount minHeight="540px" testId="lazy-trending-pins">
                <TrendingPinsWidget />
            </LazyMount>

            {/* ======= IMPACT ======= */}
            <section className="section-padding bg-secondary grain relative" data-testid="impact-section">
                <div className="container-page relative">
                    <div className="max-w-2xl mb-16">
                        <p className="overline text-muted-foreground mb-4">The Impact</p>
                        <h2 className="font-serif text-4xl sm:text-5xl tracking-tight">Travel that gives more than it takes.</h2>
                        <p className="mt-6 text-foreground/80 leading-relaxed">
                            We measure success in trees regenerated, communities sustained and species recovered — not just guest nights.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-10 gap-y-12">
                        {impactItems.map((it) => (
                            <div key={it.title} className="space-y-4" data-testid={`impact-${it.title.toLowerCase().replace(/\s+/g, "-")}`}>
                                <div className="w-12 h-12 border border-accent/40 flex items-center justify-center text-accent">
                                    <it.icon size={22} strokeWidth={1.5} />
                                </div>
                                <h3 className="font-serif text-2xl leading-snug">{it.title}</h3>
                                <p className="text-sm text-muted-foreground leading-relaxed">{it.body}</p>
                            </div>
                        ))}
                    </div>

                    <div className="mt-20 pt-12 border-t border-border grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
                        <div className="flex gap-6">
                            <Quote className="text-accent shrink-0 mt-1" size={32} strokeWidth={1} />
                            <p className="font-serif text-2xl italic leading-snug text-foreground/85">
                                "The most considered travel company we have worked with in fifteen years. Quietly principled, beautifully attentive."
                            </p>
                        </div>
                        <div className="text-sm text-muted-foreground">
                            — A. Mehta, Mumbai · returning guest
                        </div>
                    </div>
                </div>
            </section>

            {/* ======= TESTIMONIALS CAROUSEL ======= */}
            {/* Below-the-fold: hydrate only when scrolled within 600px of viewport. */}
            <LazyMount minHeight="520px" testId="lazy-testimonials">
                <TestimonialsCarousel
                    variant="light"
                    featuredOnly
                    heading="What past guests quietly say."
                    overline="Voices from the field"
                />
            </LazyMount>

            {/* ======= FAQ ======= */}
            {/* Below-the-fold: hydrate only when scrolled within 600px of viewport.
                The JSON-LD <script> is rendered separately via <Seo /> above, so
                lazy-mounting the visual FAQ accordion does NOT affect Google's
                FAQPage schema crawl. */}
            <LazyMount minHeight="640px" testId="lazy-home-faq">
                <HomeFaq />
            </LazyMount>

            {/* ======= CARBON FOOTPRINT CALCULATOR ======= */}
            <LazyMount minHeight="560px" testId="lazy-carbon-calculator">
                <CarbonCalculator />
            </LazyMount>
        </SiteLayout>
    );
}

function FeatureCard({ exp, className = "", testId, large = false, horizontal = false }) {
    return (
        <Link
            to={`/experiences/${exp.slug || exp.id}`}
            data-testid={testId}
            className={`group relative overflow-hidden bg-foreground/5 block ${className}`}
        >
            <img
                src={assetUrl(exp.image_url)}
                alt={exp.title}
                className="absolute inset-0 w-full h-full object-cover img-warm transition-transform duration-[1400ms] ease-out group-hover:scale-105"
                loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-black/10" />
            <div className={`relative h-full flex ${horizontal ? "items-end" : "items-end"} p-8 sm:p-10 text-primary-foreground`}>
                <div className={horizontal ? "max-w-2xl" : ""}>
                    <p className="overline text-primary-foreground/75 mb-3">{exp.theme}</p>
                    <h3 className={`font-serif tracking-tight ${large ? "text-4xl sm:text-5xl" : "text-2xl sm:text-3xl"}`}>
                        {exp.title}
                    </h3>
                    <p className="mt-3 text-sm text-primary-foreground/80 max-w-md leading-relaxed">{exp.tagline}</p>
                    <span className="mt-6 inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase font-semibold text-accent group-hover:translate-x-1 transition-transform duration-300">
                        Discover <ArrowRight size={14} strokeWidth={1.5} />
                    </span>
                </div>
            </div>
        </Link>
    );
}
